import React, { type JSX, useState } from "react";
import SVG from "react-inlinesvg";
import { TransitionGroup } from "react-transition-group";
import { t } from "ttag";

import iconCollapse from "../../../../img/icons/collapse.svg";
import iconExpand from "../../../../img/icons/expand.svg";
import iconReviewsYes from "../../../../img/icons/reviews-yes.svg";
import * as userAPI from "../../../api/user";
import { CSSTransitionWithRef } from "../../../common/CssTransition";
import { Link } from "../../../common/Link";
import { Trans } from "../../../common/Trans";
import { Form } from "../../../forms/Form";
import { FormInput } from "../../../forms/FormInput";
import { FormPasswordInput } from "../../../forms/FormPasswordInput";
import { FormSubmit } from "../../../forms/FormSubmit";
import { strings } from "../../../localization";
import { IWebPageURL } from "../../../models/nominals";

const BASE_CLASS = "thank-you-create-account";

const handleSubmitSetNewPassword = async (
    event: React.FormEvent<HTMLFormElement>,
    disableSubmit: boolean,
    passwordField: string,
    setPasswordErrors: React.Dispatch<React.SetStateAction<string[]>>,
    setDisableSubmit: React.Dispatch<React.SetStateAction<boolean>>,
    setFormSubmitted: React.Dispatch<React.SetStateAction<boolean>>,
) => {
    event.preventDefault();
    // Validate inputs
    if (disableSubmit) {
        return;
    }
    if (passwordField.length <= 0) {
        setPasswordErrors([t`You must enter a password to create an account`]);
        return;
    }
    // Submit form
    setDisableSubmit(true);
    try {
        await userAPI.changePassword(passwordField);
        setFormSubmitted(true);
        setDisableSubmit(true);
        setPasswordErrors([]);
    } catch (e) {
        console.error(e);
        setFormSubmitted(false);
        setDisableSubmit(false);
        setPasswordErrors([t`An unexpected error occurred. Please try again.`]);
    }
};

const handleSubmitValidateEmail = async (
    event: React.FormEvent<HTMLFormElement>,
    disableSubmit: boolean,
    email: string,
    orderHistoryURL: IWebPageURL,
    setDisableSubmit: React.Dispatch<React.SetStateAction<boolean>>,
    setFormSubmitted: React.Dispatch<React.SetStateAction<boolean>>,
) => {
    event.preventDefault();
    // Validate inputs
    if (disableSubmit) {
        return;
    }
    // Submit form
    setDisableSubmit(true);
    try {
        await userAPI.login(email, "", orderHistoryURL);
        setFormSubmitted(true);
        setDisableSubmit(true);
    } catch (e) {
        console.error(e);
        setFormSubmitted(false);
        setDisableSubmit(false);
    }
};

const handlePasswordChange = (
    event: React.FormEvent<HTMLInputElement>,
    setPasswordField: React.Dispatch<React.SetStateAction<string>>,
    setPasswordErrors: React.Dispatch<React.SetStateAction<string[]>>,
) => {
    setPasswordField(event.currentTarget.value);
    setPasswordErrors([]);
};

const handleToggleFormAccordion = (
    e: React.MouseEvent<HTMLElement>,
    setFormIsExpanded: React.Dispatch<React.SetStateAction<boolean>>,
) => {
    e.preventDefault();
    setFormIsExpanded((prev) => !prev);
};

const buildContent__AnonOrderForNewUser = (
    formSubmitted: boolean,
    formIsExpanded: boolean,
    passwordField: string,
    passwordErrors: string[],
    disableSubmit: boolean,
    email: string,
    orderHistoryURL: IWebPageURL,
    onSubmitSetNewPassword: (
        event: React.FormEvent<HTMLFormElement>,
    ) => Promise<void>,
    onPasswordChange: (event: React.FormEvent<HTMLInputElement>) => void,
    onToggleFormAccordion: (e: React.MouseEvent<HTMLElement>) => void,
): JSX.Element => {
    // Reqs Branch 1: An anonymous user placed an order using an email address that was entirely new to Oscar.
    if (formSubmitted) {
        return (
            <>
                <SVG
                    aria-hidden="true"
                    className={`${BASE_CLASS}__form-success-icon`}
                    src={iconReviewsYes}
                />
                <h2 className={`${BASE_CLASS}__title`} role="alert">
                    {t`Success! Your account has been created.`}
                </h2>
                <p className={`${BASE_CLASS}__copy`}>
                    <Trans
                        fmtString={strings.get(
                            "ORDER_THANK_YOU_B1_CREATE_ACCT_SUCCESS",
                        )}
                        data={{
                            MyOrdersLink: (content) => (
                                <Link
                                    key="orders-link"
                                    className={`${BASE_CLASS}__link`}
                                    href={orderHistoryURL}
                                    target="_top"
                                >
                                    {content}
                                </Link>
                            ),
                        }}
                    />
                </p>
            </>
        );
    }
    // Build expand/collapse body
    let accordionBody: JSX.Element | null = null;
    if (formIsExpanded) {
        accordionBody = (
            <CSSTransitionWithRef
                key="form"
                classNames="transition--fade"
                timeout={{ exit: 2000, enter: 2000 }}
            >
                <div
                    id="create-expandable-account-form"
                    className={`${BASE_CLASS}__accordion-body`}
                >
                    <p className={`${BASE_CLASS}__copy`}>
                        {strings.get("ORDER_THANK_YOU_B1_CREATE_ACCT_COPY")}
                    </p>
                    <Form
                        className={`form ${BASE_CLASS}__set-password-form`}
                        onSubmit={onSubmitSetNewPassword}
                    >
                        <FormInput
                            id="create_account_email"
                            name="create_account_email"
                            type="email"
                            label={t`Email Address:`}
                            labelPlacement="outside"
                            value={email}
                            readOnly={true}
                            disabled={true}
                        />
                        <FormPasswordInput
                            id="create_account_password"
                            name="create_account_password"
                            label={t`Password:`}
                            labelPlacement="outside"
                            value={passwordField}
                            errors={passwordErrors}
                            disabled={disableSubmit}
                            onChange={onPasswordChange}
                        />
                        <FormSubmit
                            className={`button button--full-width ${BASE_CLASS}__submit`}
                            value={t`Create Account`}
                            disabled={disableSubmit}
                        />
                    </Form>
                </div>
            </CSSTransitionWithRef>
        );
    }
    const icon = formIsExpanded ? iconCollapse : iconExpand;
    return (
        <>
            <h2 className={`${BASE_CLASS}__title`}>
                <button
                    aria-controls="create-expandable-account-form"
                    aria-expanded={formIsExpanded}
                    onClick={onToggleFormAccordion}
                >
                    {t`Let's create an account`}
                    <SVG
                        aria-hidden="true"
                        className={`${BASE_CLASS}__accordion-handle`}
                        src={icon}
                    />
                </button>
            </h2>
            <TransitionGroup>{accordionBody}</TransitionGroup>
        </>
    );
};

const buildContent__AnonOrderForNonRegisteredUser = (
    formSubmitted: boolean,
    disableSubmit: boolean,
    firstName: string,
    email: string,
    onSubmitValidateEmail: (
        event: React.FormEvent<HTMLFormElement>,
    ) => Promise<void>,
): JSX.Element => {
    // Reqs branch 2: An anonymous user placed an order using an email address that was already associated with a
    // user account in the Non-Registered status (i.e. order user does not have a password).
    if (formSubmitted) {
        return (
            <>
                <h2 className={`${BASE_CLASS}__title`}>
                    {t`Welcome back, ${firstName}`}
                </h2>
                <p className={`${BASE_CLASS}__copy`}>
                    <Trans
                        fmtString={t`Your account has been successfully created. For security purposes, we have sent an email to <UserEmail></UserEmail> to allow you to set your password.`}
                        data={{
                            UserEmail: () => (
                                <strong key="user-email">{email}</strong>
                            ),
                        }}
                    />
                </p>
            </>
        );
    }
    return (
        <Form
            className={`form ${BASE_CLASS}__validate-email-form`}
            onSubmit={onSubmitValidateEmail}
        >
            <input type="hidden" value={email} />
            <FormSubmit
                className={`button ${BASE_CLASS}__submit`}
                value={t`Create Account`}
                disabled={disableSubmit}
            />
        </Form>
    );
};

const buildContent__AnonOrderForRegisteredUser = (
    firstName: string,
    email: string,
    orderHistoryURL: IWebPageURL,
): JSX.Element => {
    // Reqs Branch 3: An anonymous user placed an order using an email address that was already associated with a
    // user account in the Registered status (i.e. order user does have a password).
    return (
        <>
            <h2 className={`${BASE_CLASS}__title`}>
                {t`Welcome back, ${firstName}`}
            </h2>
            <p className={`${BASE_CLASS}__copy`}>
                <Trans
                    fmtString={strings.get(
                        "ORDER_THANK_YOU_B3_CREATE_ACCT_COPY",
                    )}
                    data={{
                        UserEmail: () => (
                            <strong key="user-email">{email}</strong>
                        ),
                        LoginLink: (content) => (
                            <Link
                                key="login-link"
                                className={`${BASE_CLASS}__link`}
                                href={orderHistoryURL}
                                target="_top"
                            >
                                {content}
                            </Link>
                        ),
                    }}
                />
            </p>
        </>
    );
};

const buildContent__AuthenticatedUserOrder = (
    orderHistoryURL: IWebPageURL,
): JSX.Element => {
    // Reqs Branch 4: An authenticated user placed an order.
    return (
        <>
            <h2 className={`${BASE_CLASS}__title`}>
                {t`Thanks for ordering from us again.`}
            </h2>
            <p className={`${BASE_CLASS}__copy`}>
                <Trans
                    fmtString={strings.get(
                        "ORDER_THANK_YOU_B4_CREATE_ACCT_COPY",
                    )}
                    data={{
                        MyOrdersLink: (content) => (
                            <Link
                                key="orders-link"
                                className={`${BASE_CLASS}__link`}
                                href={orderHistoryURL}
                                target="_top"
                            >
                                {content}
                            </Link>
                        ),
                    }}
                />
            </p>
        </>
    );
};

const buildContent = (
    orderOwnerHasPassword: boolean,
    isAuthenticatedAsOrderOwner: boolean,
    formSubmitted: boolean,
    formIsExpanded: boolean,
    passwordField: string,
    passwordErrors: string[],
    disableSubmit: boolean,
    firstName: string,
    email: string,
    orderHistoryURL: IWebPageURL,
    onSubmitSetNewPassword: (
        event: React.FormEvent<HTMLFormElement>,
    ) => Promise<void>,
    onPasswordChange: (event: React.FormEvent<HTMLInputElement>) => void,
    onToggleFormAccordion: (e: React.MouseEvent<HTMLElement>) => void,
    onSubmitValidateEmail: (
        event: React.FormEvent<HTMLFormElement>,
    ) => Promise<void>,
): JSX.Element => {
    // See requirements in
    // https://gitlab.com/thelabnyc/tsi-sites/tsi-requirements/blob/feature_9446/tsi-tempurpedic/checkout/order-thank-you.md#create-account-form
    if (orderOwnerHasPassword) {
        if (isAuthenticatedAsOrderOwner) {
            return buildContent__AuthenticatedUserOrder(orderHistoryURL);
        }
        return buildContent__AnonOrderForRegisteredUser(
            firstName,
            email,
            orderHistoryURL,
        );
    }
    if (isAuthenticatedAsOrderOwner) {
        return buildContent__AnonOrderForNewUser(
            formSubmitted,
            formIsExpanded,
            passwordField,
            passwordErrors,
            disableSubmit,
            email,
            orderHistoryURL,
            onSubmitSetNewPassword,
            onPasswordChange,
            onToggleFormAccordion,
        );
    }
    return buildContent__AnonOrderForNonRegisteredUser(
        formSubmitted,
        disableSubmit,
        firstName,
        email,
        onSubmitValidateEmail,
    );
};

export const CreateAccount = (props: {
    firstName: string;
    email: string;
    isAuthenticatedAsOrderOwner: boolean;
    orderOwnerHasPassword: boolean;
    orderHistoryURL: IWebPageURL;
}): React.ReactNode => {
    const [disableSubmit, setDisableSubmit] = useState(false);
    const [formSubmitted, setFormSubmitted] = useState(false);
    const [passwordField, setPasswordField] = useState("");
    const [passwordErrors, setPasswordErrors] = useState<string[]>([]);
    const [formIsExpanded, setFormIsExpanded] = useState(true);

    const onSubmitSetNewPassword = async (
        event: React.FormEvent<HTMLFormElement>,
    ) => {
        await handleSubmitSetNewPassword(
            event,
            disableSubmit,
            passwordField,
            setPasswordErrors,
            setDisableSubmit,
            setFormSubmitted,
        );
    };

    const onSubmitValidateEmail = async (
        event: React.FormEvent<HTMLFormElement>,
    ) => {
        await handleSubmitValidateEmail(
            event,
            disableSubmit,
            props.email,
            props.orderHistoryURL,
            setDisableSubmit,
            setFormSubmitted,
        );
    };

    const onPasswordChange = (event: React.FormEvent<HTMLInputElement>) => {
        handlePasswordChange(event, setPasswordField, setPasswordErrors);
    };

    const onToggleFormAccordion = (e: React.MouseEvent<HTMLElement>) => {
        handleToggleFormAccordion(e, setFormIsExpanded);
    };

    return (
        <div className={BASE_CLASS}>
            {buildContent(
                props.orderOwnerHasPassword,
                props.isAuthenticatedAsOrderOwner,
                formSubmitted,
                formIsExpanded,
                passwordField,
                passwordErrors,
                disableSubmit,
                props.firstName,
                props.email,
                props.orderHistoryURL,
                onSubmitSetNewPassword,
                onPasswordChange,
                onToggleFormAccordion,
                onSubmitValidateEmail,
            )}
        </div>
    );
};
